import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAtlas } from "@/context/AtlasContext";
import { PageHeader } from "@/components/shared/PageHeader";
import { Switch } from "@/components/ui/switch";
import { Contact, Target, Boxes, Megaphone, Activity } from "lucide-react";
import { toast } from "sonner";

const ICONS = { crm: Contact, tactics: Target, inventory: Boxes, marketing: Megaphone };
const FEATURE_ICONS = { pulse_activity: Activity };

export default function Applications() {
  const { loadContext, activeTenantId } = useAtlas();
  const [apps, setApps] = useState([]);
  const [features, setFeatures] = useState([]);
  const load = () => {
    api.get("/admin/applications").then((r) => setApps(r.data)).catch(() => {});
    api.get("/admin/features").then((r) => setFeatures(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const toggle = async (app, enabled) => {
    try { await api.put(`/admin/applications/${app.key}`, { enabled }); toast.success(`${app.name} ${enabled ? "enabled" : "disabled"}`); load(); loadContext(activeTenantId); }
    catch (e) { toast.error(apiError(e)); }
  };
  const toggleFeature = async (f, enabled) => {
    try { await api.put(`/admin/features/${f.key}`, { enabled }); toast.success(`${f.name} ${enabled ? "enabled" : "disabled"}`); load(); loadContext(activeTenantId); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div>
      <PageHeader title="Applications" subtitle="Enable or disable applications and add-ons for this tenant" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="admin-applications-grid">
        {apps.map((a) => {
          const I = ICONS[a.key] || Contact;
          return (
            <div key={a.key} className="bg-white border border-slate-200 rounded-xl p-5 flex items-start gap-4" data-testid={`app-config-${a.key}`}>
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0"><I className="w-5 h-5 text-blue-600" /></div>
              <div className="flex-1">
                <div className="font-heading font-semibold text-slate-900">{a.name}</div>
                <p className="text-sm text-slate-500 mt-0.5">{a.description}</p>
                <p className="text-[11px] text-slate-400 mt-1 font-mono">access: {a.access_permission}</p>
              </div>
              <Switch checked={a.enabled} onCheckedChange={(v) => toggle(a, v)} data-testid={`app-toggle-${a.key}`} />
            </div>
          );
        })}
      </div>

      {features.length > 0 && (
        <div className="mt-8" data-testid="admin-features-section">
          <h2 className="text-lg font-heading font-semibold text-slate-900 mb-1">Feature add-ons</h2>
          <p className="text-sm text-slate-500 mb-4">Paid capabilities layered onto a module. Teammates still need the matching permission to use them.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="admin-features-grid">
            {features.map((f) => {
              const I = FEATURE_ICONS[f.key] || Activity;
              return (
                <div key={f.key} className="bg-white border border-slate-200 rounded-xl p-5 flex items-start gap-4" data-testid={`feature-config-${f.key}`}>
                  <div className="w-10 h-10 rounded-lg bg-violet-50 flex items-center justify-center flex-shrink-0"><I className="w-5 h-5 text-violet-600" /></div>
                  <div className="flex-1">
                    <div className="font-heading font-semibold text-slate-900">{f.name}</div>
                    <p className="text-sm text-slate-500 mt-0.5">{f.description}</p>
                  </div>
                  <Switch checked={f.enabled} onCheckedChange={(v) => toggleFeature(f, v)} data-testid={`feature-toggle-${f.key}`} />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
